import { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { FileText, Play } from "lucide-react";
import { API } from "../../config";
import { cacheMedia, getCachedMedia } from "../../db";

function useMediaUrl(note, token) {
    const [src, setSrc] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let objectUrl = null;
        let blobUrl = null;

        async function loadMedia() {
            if (!note) return;

            try {
                // If note has media_key (cloud storage)
                if (note.media_key && token) {
                    setLoading(true);

                    // Step 1: Check cache first
                    const cachedBlob = await getCachedMedia(note.media_key);
                    if (cachedBlob) {
                        blobUrl = URL.createObjectURL(cachedBlob);
                        setSrc(blobUrl);
                        setLoading(false);
                        console.log(`Loaded from cache: ${note.media_key}`);
                        return;
                    }

                    // Step 2: Get presigned URL and fetch from S3
                    try {
                        const response = await axios.post(
                            `${API}/notes/download-url`,
                            { media_key: note.media_key },
                            { headers: { Authorization: `Bearer ${token}` } }
                        );

                        // Fetch the actual blob from S3
                        const mediaResponse = await fetch(response.data.download_url);
                        if (!mediaResponse.ok) throw new Error('Failed to fetch media');

                        const blob = await mediaResponse.blob();

                        // Step 3: Cache the blob for future use
                        const contentType = note.type === 'video' ? 'video/mp4' : 'image/jpeg';
                        cacheMedia(note.media_key, blob, contentType); // Don't await

                        // Create blob URL for display
                        blobUrl = URL.createObjectURL(blob);
                        setSrc(blobUrl);
                        setLoading(false);
                        return;
                    } catch (err) {
                        console.error("Failed to download media:", err);
                        setError(true);
                        setLoading(false);
                        return;
                    }
                }

                // If note has local blob (pending upload or legacy)
                if (note.blob) {
                    if (note.blob instanceof Blob) {
                        objectUrl = URL.createObjectURL(note.blob);
                        setSrc(objectUrl);
                        setLoading(false);
                        return;
                    }
                }

                // Legacy notes without cloud storage
                setError(true);
                setLoading(false);
            } catch (e) {
                console.error("Failed to load media:", e);
                setError(true);
                setLoading(false);
            }
        }
        loadMedia();
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [note, token]);

    return { src, error, loading };
}

export function MediaPreview({ note, token }) {
    const { src, error, loading } = useMediaUrl(note, token);

    if (loading) return <div className="animate-pulse bg-slate-200 w-full h-full" />;
    if (error) return <div className="text-[10px] text-slate-400 text-center px-4 py-2 bg-slate-100 w-full h-full flex items-center">Media unavailable</div>;
    if (!src) return <div className="animate-pulse bg-slate-200 w-full h-full" />;

    if (note.type === 'video') {
        return (
            <div className="relative w-full h-full bg-black flex items-center justify-center">
                <video
                    src={src}
                    className="w-full h-full object-cover"
                    playsInline
                    muted
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Play size={24} className="text-white fill-white opacity-80" />
                </div>
            </div>
        );
    }

    if (note.type === 'pdf') {
        return (
            <div className="flex flex-col items-center justify-center gap-2 p-4 bg-slate-100 w-full h-full">
                <FileText size={32} className="text-red-500" />
                <span className="text-xs text-slate-600 truncate max-w-full">{note.content}</span>
            </div>
        );
    }

    return <img src={src} alt="Note" className="w-full h-full object-cover" />;
}

MediaPreview.propTypes = {
    note: PropTypes.object,
    token: PropTypes.string
};

export function MediaDisplay({ note, token }) {
    const { src, error, loading } = useMediaUrl(note, token);

    if (loading) return <div className="animate-pulse bg-slate-800 w-full h-64 rounded" />;
    if (error) return (
        <div className="text-white flex flex-col items-center gap-2">
            <p>Failed to load media file.</p>
            <p className="text-xs text-slate-400">It might not be available in the cloud.</p>
        </div>
    );
    if (!src) return <div className="animate-pulse bg-slate-800 w-full h-64 rounded" />;

    if (note.type === 'video') {
        return (
            <video
                src={src}
                className="w-full h-full max-h-[85vh] object-contain"
                controls
                autoPlay
                playsInline
            />
        );
    }

    if (note.type === 'pdf') {
        return (
            <div className="flex flex-col items-center justify-center gap-4 p-8">
                <FileText size={64} className="text-red-500" />
                <span className="text-white">{note.content}</span>
                <a
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    Open PDF
                </a>
            </div>
        );
    }

    return (
        <img
            src={src}
            alt="Full view"
            className="w-full h-full max-h-[85vh] object-contain"
        />
    );
}

MediaDisplay.propTypes = {
    note: PropTypes.object,
    token: PropTypes.string
};
